import { consoleLog } from 'services/log/utils';
import { transformStringToKey } from 'utils/pql';

import type { StoreSet, StoreGet } from './index';

type RegistryMap = Record<string, string[]>;

export type SearchRegistrySlice = {
  registry: RegistryMap;
  registerComponent: (groupKey: string, pqlQuery: string) => void;
  deregisterComponent: (groupKey: string, pqlQuery: string) => void;
  clearRegistry: () => void;
  isAnySearchInProgress: (groupKey: string) => boolean;
};

const transform = transformStringToKey;

const addQuery = (registry: RegistryMap, groupKey: string, query: string): RegistryMap => {
  const tQuery = transform(query) as string;
  const tKey = transform(groupKey) as string;
  if (registry[tKey] && registry[tKey].includes(tQuery)) {
    return registry;
  }
  return {
    ...registry,
    [tKey]: [...(registry[tKey] || []), tQuery],
  };
};

const removeQuery = (registry: RegistryMap, groupKey: string, query: string): RegistryMap => {
  const tQuery = transform(query) as string;
  const tKey = transform(groupKey) as string;
  if (!registry[tKey] || !registry[tKey].includes(tQuery)) {
    return registry;
  }
  return {
    ...registry,
    [tKey]: registry[tKey].filter((q) => q !== tQuery),
  };
};

// tracks ongoing searches
const createSearchRegistry = (set: StoreSet, get: StoreGet): SearchRegistrySlice => ({
  registry: {},

  registerComponent: (groupKey, pqlQuery) => {
    if (!groupKey || !pqlQuery) {
      consoleLog('ERROR', 'searchRegistry.registerComponent: invalid arguments', {
        groupKey,
        pqlQuery,
      });
      return;
    }
    set((prev) => ({
      searchRegistry: {
        ...prev.searchRegistry,
        registry: addQuery(prev.searchRegistry.registry, groupKey, pqlQuery),
      },
    }));
  },

  deregisterComponent: (groupKey, pqlQuery) => {
    if (!groupKey || !pqlQuery) {
      consoleLog('ERROR', 'searchRegistry.deregisterComponent: invalid arguments', {
        groupKey,
        pqlQuery,
      });
      return;
    }
    set((prev) => ({
      searchRegistry: {
        ...prev.searchRegistry,
        registry: removeQuery(prev.searchRegistry.registry, groupKey, pqlQuery),
      },
    }));
  },

  clearRegistry: () => {
    set((prev) => ({ searchRegistry: { ...prev.searchRegistry, registry: {} } }));
  },

  isAnySearchInProgress: (groupKey) => get().searchRegistry.registry[transform(groupKey) as string]?.length > 0,
});

export default createSearchRegistry;
